import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import { LeftMenu } from "./LeftMenu";
import { MenuBar } from "./MenuBar";
import { useUser } from "../User/UserObject";
import { MenuProps } from "./Menu.types";
import { useMenuContext } from "./MenuContext";
import MenuDrawerHeader from "./MenuDrawerHeader";
import { useDockedSidebar } from "../../hooks/useDockedSidebar";
import { useAppSidebarWidthCssVar } from "../../hooks/useAppSidebarWidthCssVar";

import "./Menu.css";

function Menu({ availableCourses, initialCourse, onSelectCourse }: MenuProps) {
  const { user } = useUser();
  const isDocked = useDockedSidebar();
  const {
    showMenu,
    setShowMenu,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarCompact,
    setSidebarCompact,
  } = useMenuContext();
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);

  const hideMenu = () => {
    setShowMenu(false);
  };

  useAppSidebarWidthCssVar({ isDocked, sidebarCollapsed });

  useEffect(() => {
    setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  useEffect(() => {
    setSidebarCompact(
      sidebarCollapsed && (isDocked || showMenu)
    );
  }, [isDocked, showMenu, sidebarCollapsed, setSidebarCompact]);

  useEffect(() => {
    if (isDocked) {
      setShowMenu(false);
    }
  }, [isDocked, setShowMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDocked) {
        return;
      }

      const isClickOutsideMenu =
        menuRef.current && !menuRef.current.contains(event.target as Node);
      const isClickOnMenuButton =
        menuButtonRef.current &&
        menuButtonRef.current.contains(event.target as Node);

      if (isClickOnMenuButton) {
        return;
      }

      if (isClickOutsideMenu) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDocked, setShowMenu]);

  const isAdmin = user && user.IsAdmin;
  const isCollapsedVisible = sidebarCollapsed && (isDocked || showMenu);
  const sidebarClass = clsx("app-sidebar", "menu", {
    "app-sidebar--docked": isDocked,
    "app-sidebar--collapsed": isCollapsedVisible,
    show: isDocked || showMenu,
  });

  return (
    <>
      {!isDocked && <div className="appTopBar" aria-hidden="true" />}
      {!isDocked && (
        <MenuBar
          ref={menuButtonRef}
          showMenu={showMenu}
          setShowMenu={setShowMenu}
        />
      )}
      {!isDocked && (
        <div
          className={`menu-overlay ${showMenu ? "show" : ""}`}
          onClick={hideMenu}
          aria-hidden="true"
        />
      )}
      <div
        ref={menuRef}
        className={clsx("menu-wrapper", isDocked && "menu-wrapper--docked")}
      >
        <div className={sidebarClass}>
          <MenuDrawerHeader
            variant={isDocked ? "docked" : "drawer"}
            compact={sidebarCompact}
            onClose={hideMenu}
          />
          <LeftMenu
            availableCourses={availableCourses}
            showAdmin={isAdmin ?? false}
            course={initialCourse}
            onSelectCourse={onSelectCourse}
            hideMenu={hideMenu}
            isFloatingDrawer={!isDocked}
          />
        </div>
      </div>
    </>
  );
}

export default Menu;
